/**
 * Google Analytics 4 の最小ラッパー。
 *
 * 設計方針は Turnstile（`components/Turnstile.tsx`）と同じで、
 * **測定ID が未設定でも壊れない**。`VITE_GA4_MEASUREMENT_ID` が無ければ
 * スクリプトを読み込まず、すべての関数が no-op になる。ID を入れて
 * ビルドし直した瞬間に有効化される。
 *
 * Vite はビルド時に値を焼き込むため、ID は build arg で渡す
 * （`VITE_API_URL` / `VITE_TURNSTILE_SITE_KEY` と同じ経路）。
 *
 * ## クエリ文字列を送らないこと（重要）
 *
 * GA4 は既定で `location.href` をそのまま `page_location` として送る。
 * 本アプリには `/reset-password?token=...` と `/verify-email?token=...` があり、
 * 既定のまま計測すると **パスワード再設定トークンが解析サービスへ渡る**。
 * そのため `send_page_view: false` で自動送信を止め、
 * **パスだけに正規化した page_view を手動で送る**。
 *
 * ## 自己除外（社内アクセスをデータに混ぜない）
 *
 * `https://kinlog.jp/?analytics=off` を一度開くと、**その端末では以後ずっと計測しない**。
 * `?analytics=on` で解除。判定は localStorage なので、
 * IP が変わっても・スマートフォンでも・PWA から開いても効く
 * （GA4 の IP による内部トラフィック除外では、回線が変わると漏れる）。
 *
 * localStorage はオリジン単位なので、**一度どこかのページで設定すれば
 * 静的なブログページ（/blog/）にも同じ判定が効く**。
 * ただしブログ側は SPA の外にある独立した HTML なので、同じ判定を
 * `scripts/build-blog.mjs` にも書いてある。**片方だけ直さないこと。**
 */

const MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;

/** 自己除外フラグの保存先。build-blog.mjs と同じ値を使うこと。 */
const OPT_OUT_KEY = 'kinlog_analytics_opt_out';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** 一時的なお知らせを画面に出す（自己除外の切り替え時のみ。通常の閲覧では出ない）。 */
const showNotice = (message: string): void => {
  const el = document.createElement('div');
  el.textContent = message;
  el.style.cssText = [
    'position:fixed',
    'left:50%',
    'bottom:24px',
    'transform:translateX(-50%)',
    'z-index:2147483647',
    'background:#1A2E4A',
    'color:#fff',
    'padding:12px 20px',
    'border-radius:10px',
    'font-size:14px',
    'line-height:1.6',
    'max-width:90vw',
    'box-shadow:0 6px 24px rgba(0,0,0,.28)',
  ].join(';');
  const attach = () => {
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 6000);
  };
  if (document.body) attach();
  else document.addEventListener('DOMContentLoaded', attach);
};

/**
 * URL の `?analytics=off|on` を処理し、この端末を計測対象から外すか判定する。
 * @returns true なら計測しない
 */
const isOptedOut = (): boolean => {
  try {
    const param = new URLSearchParams(window.location.search).get('analytics');

    if (param === 'off') {
      localStorage.setItem(OPT_OUT_KEY, '1');
      showNotice('このブラウザではアクセス解析を無効にしました（解除は ?analytics=on）');
      return true;
    }
    if (param === 'on') {
      localStorage.removeItem(OPT_OUT_KEY);
      showNotice('このブラウザのアクセス解析を有効に戻しました');
      return false;
    }

    return localStorage.getItem(OPT_OUT_KEY) === '1';
  } catch {
    // プライベートモード等で localStorage が使えない場合。
    // 計測を止める理由にはならないので、通常どおり計測する。
    return false;
  }
};

/** 計測が有効か（測定ID が設定されているか） */
export const isAnalyticsEnabled = (): boolean => Boolean(MEASUREMENT_ID);

/** クエリ・ハッシュを捨ててパスだけにする（トークン等を送らないため） */
const toSafePath = (pathname: string): string => pathname || '/';

let initialized = false;

/**
 * gtag.js を読み込んで初期化する。アプリ起動時に1回だけ呼ぶ。
 * 未設定時は警告だけ出して何もしない（「入れたつもりで効いていない」に気づけるように）。
 */
export const initAnalytics = (): void => {
  if (initialized) return;

  // 測定IDの有無より先に判定する。ID を入れる前に ?analytics=off を
  // 実行しておけば、**ID を入れた瞬間から除外が効いている**状態にできる。
  if (isOptedOut()) {
    initialized = true;
    console.info('[analytics] このブラウザは計測対象から除外されています（?analytics=on で解除）');
    return;
  }

  if (!MEASUREMENT_ID) {
    if (import.meta.env.PROD) {
      console.warn(
        '[analytics] VITE_GA4_MEASUREMENT_ID が未設定のため、アクセス解析は無効です。',
      );
    }
    return;
  }

  initialized = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // gtag は arguments オブジェクトをそのまま push する仕様のため、
  // アロー関数（arguments を持たない）ではなく function 式で定義する。
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID, {
    // 自動 page_view を止める。SPA では初回しか撃たれないうえ、
    // クエリ付き URL がそのまま送られてしまうため（上記コメント参照）。
    send_page_view: false,
  });
};

/**
 * 画面遷移を計測する。パスのみを送り、クエリ・ハッシュは送らない。
 */
export const trackPageView = (pathname: string, title?: string): void => {
  if (!window.gtag || !MEASUREMENT_ID) return;

  const path = toSafePath(pathname);
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: title || document.title,
  });
};

/**
 * 任意のイベントを送る。
 * パラメータに個人情報・トークンを入れないこと（GA4 の規約違反かつ漏洩になる）。
 */
export const trackEvent = (name: string, params?: Record<string, unknown>): void => {
  if (!window.gtag || !MEASUREMENT_ID) return;
  window.gtag('event', name, params || {});
};

// ---------------------------------------------------------------------------
// コンバージョン（獲得の評価に使う一次指標）
// ---------------------------------------------------------------------------

/**
 * 事業者登録の完了 = 14日間トライアルの開始。
 * SEO/コンテンツ施策の良し悪しを判定する **主指標**。
 */
export const trackSignUp = (): void => {
  trackEvent('sign_up', { method: 'company_register' });
};

/**
 * Stripe Checkout へ遷移した時点。
 *
 * 実際の課金成立（`purchase`）はここでは送らない。決済の確定は Stripe の
 * webhook でしか正しく判定できず、戻り先ページで撃つと未決済でも計上され、
 * 再読み込みで二重計上になる。**売上の正は Stripe 側**とし、
 * ここでは「課金導線に進んだ数」までを計測する。
 */
export const trackBeginCheckout = (context: 'new' | 'from_beta'): void => {
  trackEvent('begin_checkout', { checkout_context: context });
};
